import { Fragment, useEffect, useRef, useState } from "react";

import { useTrack } from "@/hooks/useTrack/useTrack";
import {
  Button,
  Combobox,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  Spinner,
  useComboboxAnchor,
} from "@aircall/ds";
import { Pencil, X } from "@aircall/react-icons";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { Section, SectionHeader, SectionTitle } from "@/blocks/Section";
import { useLinkTagsToCall } from "@/hooks/useLinkToTags/useLinkTagsToCall";
import {
  filterOutAdminPortalTags,
  filterOutSystemTags,
  transformTagData,
  useTags,
} from "@/hooks/useTags/useTags";
import { TagOption } from "@/hooks/useTags/useTags.decl";
import { TagsList } from "../TagList";
import { RouteParams } from "../Transcription/Transcription.decl";

import { MultiSelectTagChip, MultiSelectTagItem } from "./MultiSelectTagItem";
import { TAG_PAGE_ROUTE } from "./SectionTags.constants";
import { TagSelectorProps } from "./SectionTags.decl";

import { RenderMultiple } from "@/components/RenderMultiple";
import { SkeletonTag } from "@/components/Skeleton";
import { useCall } from "@/hooks/useCall/useCall";
import { useGetLine } from "@/hooks/useGetLine/useGetLine";
import { useBaseStore } from "@/store/store";
import { isEqual, sortBy } from "lodash";

/**
 * The inline multi-select tag editor, assembled directly from the DS Combobox
 * compound parts. `useComboboxAnchor` pins the popup to the chips container so
 * the dropdown takes the full input width and opens below it (Base UI flips
 * `data-chips` → `min-w-(--anchor-width)`), instead of overlapping the input.
 *
 * Filtering is owned by the consumer (`onSearch` drives `items`); selection is
 * the source of truth (`value`), so chips keep rendering even when the matching
 * option has been filtered out of `items`. `isItemEqualToValue` compares by the
 * stable `value` key so re-selecting an option toggles it off instead of
 * duplicating it.
 */
function TagSelector({
  items,
  value,
  loading,
  onSearch,
  onValueChange,
  onClose,
}: TagSelectorProps) {
  const anchorRef = useComboboxAnchor();
  const inputRef = useRef<HTMLInputElement>(null);
  // Control `open` + `inputValue` ourselves. When the chips input lives outside
  // the popup (our layout), Base UI's multi-select closes the popup on item
  // press *while a query is active* instead of just clearing the query — which
  // dropped the selection. We keep the editor open in that case and reset the
  // query, mirroring the unfiltered selection path.
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState("");

  // Auto-focus the search input when the selector opens (e.g. after clicking
  // the edit pencil) so the user can start typing right away.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function handleQueryChange(nextQuery: string) {
    setQuery(nextQuery);
    onSearch(nextQuery);
  }

  function handleValueChange(selected: TagOption[]) {
    onValueChange(selected);
    // Reset the search after a pick so the full list returns and the next
    // selection starts from a clean input.
    if (query) {
      handleQueryChange("");
    }
  }

  function handleOpenChange(
    nextOpen: boolean,
    eventDetails?: { reason?: string }
  ) {
    // Base UI requests a close on item press when filtering; keep the editor
    // open so multi-select keeps working like the unfiltered path.
    if (!nextOpen && eventDetails?.reason === "item-press") {
      setOpen(true);
      return;
    }

    setOpen(nextOpen);

    if (!nextOpen) {
      onClose();
    }
  }

  return (
    <Combobox<TagOption, true>
      multiple
      open={open}
      inputValue={query}
      items={items}
      value={value}
      onValueChange={handleValueChange}
      onInputValueChange={handleQueryChange}
      onOpenChange={handleOpenChange}
      isItemEqualToValue={(a, b) => a.value === b.value}
      filter={null}
    >
      <ComboboxChips ref={anchorRef} data-test="tag-search-box">
        {value.map((option) => (
          <Fragment key={option.value}>
            <MultiSelectTagChip option={option} />
          </Fragment>
        ))}
        <ComboboxChipsInput ref={inputRef} data-test="tag-search-input" />
        {value.length > 0 ? (
          <button
            type="button"
            data-test="tag-search-clear"
            aria-label="Clear all"
            className="ml-auto inline-flex cursor-pointer items-center text-muted-foreground"
            // Prevent the button from stealing focus from the input: otherwise
            // the input blurs, Base UI closes the popup, and the save runs
            // before this click empties the selection — dropping the clear.
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              onValueChange([]);
            }}
          >
            <X className="size-4" />
          </button>
        ) : (
          <ComboboxTrigger data-test="tag-search-chevron" className="ml-auto" />
        )}
      </ComboboxChips>

      <ComboboxContent anchor={anchorRef} data-test="tag-search-content">
        {loading ? (
          <div
            className="flex justify-center p-3"
            data-test="tag-search-loader"
          >
            <Spinner />
          </div>
        ) : (
          <>
            <ComboboxEmpty data-test="tag-search-empty">0 result</ComboboxEmpty>
            <ComboboxList data-test="tag-search-menu">
              {items.map((option) => (
                <ComboboxItem
                  key={option.value}
                  value={option}
                  data-test={`tag-search-item-${option.value}`}
                >
                  <MultiSelectTagItem option={option} />
                </ComboboxItem>
              ))}
            </ComboboxList>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
}

function SectionTagsBody() {
  const { t } = useTranslation();
  const { track } = useTrack();
  const { callId } = useParams<RouteParams>();

  const { call } = useCall();
  const { lineId } = useGetLine();
  const activeTags = useBaseStore((state) => state.activeTags);
  const tagIds = useBaseStore((state) => state.tagIds);
  const setTagIds = useBaseStore((state) => state.setTagIds);
  const isMutationLoading = useBaseStore((state) => state.mutationLoading);
  const companyTags = useBaseStore((state) => state.companyTags);

  const [displayTagSelector, setDisplayTagSelector] = useState(false);
  const [options, setOptions] = useState<TagOption[]>(() =>
    transformTagData(companyTags)
  );

  useEffect(() => {
    // Only resync from activeTags while the inline editor is closed (initial
    // load + after a save refetch) so an async refetch can't clobber an
    // in-progress edit.
    if (!displayTagSelector) {
      setTagIds(activeTags.map((tag) => tag.id));
    }
  }, [activeTags, displayTagSelector, setTagIds]);

  const { updateTags } = useLinkTagsToCall({ callId: callId! });

  const { loading: isLoading } = useTags();

  const handleOnClick = () => {
    setDisplayTagSelector(true);
  };

  const handleOnClose = () => {
    // Read the latest selection straight from the store: a selection and the
    // close can land in the same event (e.g. Clear-all then focus-out), where
    // the `tagIds` closure would still hold the pre-change value and the save
    // would be skipped (dropping the change). `getState()` is always fresh.
    const latestTagIds = useBaseStore.getState().tagIds;
    const activeTagIds = activeTags.map((tag) => tag.id);
    const hasDifferentTags = !isEqual(
      sortBy(activeTagIds),
      sortBy(latestTagIds)
    );

    if (hasDifferentTags) {
      updateTags(latestTagIds);

      track("conversation_info_tag_updated", {
        call_uuid: call?.uuid,
        number_id: lineId,
        // Content-based, not count-based: any tag id not previously active means
        // tags were added (incl. replacing); otherwise it was a removal.
        update_type: latestTagIds.some((id) => !activeTagIds.includes(id))
          ? "added"
          : "deleted",
      });
    }

    setDisplayTagSelector(false);
    setOptions(transformTagData(companyTags));
  };

  const handleOnValueChange = (selected: TagOption[]) => {
    setTagIds(selected.map((option) => option.value));
  };
  const handleOnSearch = (query: string) => {
    if (!query) {
      return setOptions(transformTagData(companyTags));
    }

    setOptions(
      transformTagData(companyTags).filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  if (isLoading) {
    return (
      <RenderMultiple
        data-test="section-tags-loader"
        className="gap-2"
        direction="horizontal"
        quantity={2}
      >
        {(index) => <SkeletonTag key={index} />}
      </RenderMultiple>
    );
  }

  if (!companyTags.filter(filterOutAdminPortalTags).length) {
    return (
      <p
        className="text-sm font-normal text-muted-foreground"
        data-test="no-tag-added-text"
      >
        <Trans i18nKey="call_context.tag_page_message">
          You haven't added tags yet. If you want to organize calls in category,
          create tags in the
          <a
            href={TAG_PAGE_ROUTE}
            target="_blank"
            className="text-primary hover:underline"
            data-test="tag_page_link"
            rel="noreferrer"
          />
          .
        </Trans>
      </p>
    );
  }

  if (displayTagSelector) {
    const allOptions = transformTagData(companyTags);
    const selectedOptions = tagIds.reduce<TagOption[]>((acc, id) => {
      const option = allOptions.find((candidate) => candidate.value === id);
      if (option) {
        acc.push(option);
      }
      return acc;
    }, []);

    return (
      <TagSelector
        loading={isMutationLoading}
        items={options}
        value={selectedOptions}
        onSearch={handleOnSearch}
        onValueChange={handleOnValueChange}
        onClose={handleOnClose}
      />
    );
  }

  // Only tags with a name are displayable (TagsListItem renders null otherwise). Filtering
  // them out here keeps the count in sync with what renders, so a call whose only tags are
  // nameless falls into the clean "no tags" state instead of an empty tag list next to a
  // floating edit button. [CI-6183]
  const filteredActiveTags = activeTags
    .filter(filterOutSystemTags)
    .filter((tag) => Boolean(tag.name));

  if (!filteredActiveTags.length) {
    return (
      <div className="flex items-center gap-4">
        <span className="text-sm font-normal text-muted-foreground">
          {t("call_context.no_tags")}
        </span>
        <Button
          data-test="section-tags-edit-tags-button"
          onClick={handleOnClick}
          size="icon-sm"
          variant="ghost"
        >
          <Pencil className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <TagsList tags={filteredActiveTags} />
      <Button
        data-test="section-tags-edit-tags-button"
        onClick={handleOnClick}
        size="icon-sm"
        variant="ghost"
      >
        <Pencil className="size-4" />
      </Button>
    </div>
  );
}

export function SectionTags() {
  const { t } = useTranslation();

  return (
    <Section data-test="tags-section">
      <SectionHeader>
        <SectionTitle>{t("call_context.tags")}</SectionTitle>
      </SectionHeader>
      <SectionTagsBody />
    </Section>
  );
}
